import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Award,
  Building2,
  CheckCircle2,
  Clock,
  Download,
  ExternalLink,
  FileText,
  HelpCircle,
  ImageIcon,
  Landmark,
  Mail,
  MapPin,
  Medal,
  Navigation,
  Phone,
  Route,
  ShieldCheck,
  Star,
  Target,
  Trophy,
  Users,
} from "lucide-react";
import "../../styles/institutional-pages.css";

const fadeUp = {
  hidden: { opacity: 0, y: 28, filter: "blur(6px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.58, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.075, delayChildren: 0.04 } } };
const badgeThemes = [
  "bg-emerald-100 text-emerald-800 border-emerald-200",
  "bg-lime-100 text-lime-800 border-lime-200",
  "bg-blue-100 text-blue-800 border-blue-200",
  "bg-amber-100 text-amber-800 border-amber-200",
  "bg-purple-100 text-purple-800 border-purple-200",
  "bg-red-100 text-red-800 border-red-200",
];
const iconPool = [ShieldCheck, Award, Users, Target, Trophy, Star, Medal, Landmark, CheckCircle2];

function safeImage(src, fallback = "/fpb.png") {
  if (!src) return fallback;
  if (src.startsWith("http") || src.startsWith("/")) return src;
  return `/${src.replace(/^\/+/, "")}`;
}

function toParagraphs(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || "").split(/\n{2,}/).map((item) => item.trim()).filter(Boolean);
}

function extractIframeSrc(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  const match = text.match(/src=["']([^"']+)["']/i);
  return match ? match[1] : text;
}

function mapEmbedUrl(mapUrl, address) {
  const raw = extractIframeSrc(mapUrl);
  if (raw && raw.includes("google.com/maps")) {
    if (raw.includes("/embed") || raw.includes("output=embed")) return raw;
    return `${raw}${raw.includes("?") ? "&" : "?"}output=embed`;
  }
  if (raw && raw.startsWith("http")) return raw;
  const query = address || "Federal Polytechnic Bida, Niger State, Nigeria";
  return `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
}

function getBadgeIcon(item, index) {
  const rawIcon = String(item.icon || item.meta || "").trim();
  if (rawIcon && rawIcon.length <= 4) return rawIcon;
  const Icon = iconPool[index % iconPool.length];
  return <Icon size={17} />;
}

function Section({ id, eyebrow, title, icon: Icon = ShieldCheck, children, dark = false }) {
  return (
    <motion.section
      id={id}
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.14 }}
      className={`${dark ? "bg-emerald-950 text-white border-emerald-900" : "bg-white text-slate-950 border-slate-200"} rounded-3xl border p-5 shadow-lg shadow-slate-200/70 md:p-8`}
    >
      <div className="mb-5">
        {eyebrow && <p className={`${dark ? "text-emerald-200" : "text-emerald-700"} text-xs font-black uppercase tracking-[0.25em]`}>{eyebrow}</p>}
        <h2 className="mt-2 flex items-center gap-3 text-2xl font-black tracking-tight md:text-3xl">
          <span className={`${dark ? "bg-white/10 text-emerald-100" : "bg-emerald-50 text-emerald-700"} rounded-2xl p-2`}><Icon size={24} /></span>
          {title}
        </h2>
      </div>
      <div className={`${dark ? "bg-white/10" : "bg-slate-100"} h-1 w-full overflow-hidden rounded-full`}>
        <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="h-full origin-left bg-gradient-to-r from-emerald-600 via-lime-400 to-teal-400" />
      </div>
      <div className="mt-6">{children}</div>
    </motion.section>
  );
}

function ProgressBadges({ items = [], reduceMotion }) {
  const list = (items || []).filter((item) => item && (item.title || item.value || item.description));
  if (!list.length) return null;
  return (
    <div className="mb-5 flex flex-wrap gap-3" aria-label="Unit progress and achievements">
      {list.map((item, index) => {
        const label = item.value ? `${item.value} ${item.title || ""}`.trim() : (item.title || item.description);
        return (
          <motion.span
            key={`${label}-${index}`}
            whileHover={reduceMotion ? undefined : { y: -3, scale: 1.03 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-bold shadow-sm ${badgeThemes[index % badgeThemes.length]}`}
            title={item.description || label}
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/70 shadow-sm">{getBadgeIcon(item, index)}</span>
            <span>{label}</span>
          </motion.span>
        );
      })}
    </div>
  );
}

function UnitMapSection({ contact = {}, mapPoints = [], reduceMotion }) {
  const mainPoint = mapPoints.find((item) => item.mapUrl || item.address) || null;
  const address = mainPoint?.address || contact.address || "Federal Polytechnic Bida, Niger State, Nigeria";
  const embedSrc = mapEmbedUrl(mainPoint?.mapUrl || contact.mapUrl, address);
  const title = mainPoint?.title || "Unit Location";
  return (
    <Section id="map" eyebrow="Locate the unit" title="Map & Unit Office Location" icon={MapPin}>
      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <motion.div whileHover={reduceMotion ? undefined : { scale: 1.01 }} className="min-h-[320px] overflow-hidden rounded-2xl border border-emerald-100 bg-emerald-50 shadow-inner">
          <iframe title={title} src={embedSrc} width="100%" height="100%" loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="h-[320px] w-full border-0 md:h-[420px]" />
        </motion.div>
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-emerald-700 p-3 text-white"><Navigation size={22} /></div>
              <div>
                <h3 className="font-black text-slate-900">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{address}</p>
              </div>
            </div>
          </div>
          {(mapPoints.length ? mapPoints : [{ title: "Unit Office", address }]).map((point, index) => (
            <motion.div key={index} whileHover={reduceMotion ? undefined : { x: 4 }} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-emerald-50 p-2 text-emerald-700"><Route size={18} /></div>
                <div>
                  <h4 className="font-bold text-slate-900">{point.title || `Location ${index + 1}`}</h4>
                  <p className="mt-1 text-sm leading-6 text-slate-600">{point.address || point.note || address}</p>
                  {point.note && <p className="mt-1 text-xs font-bold uppercase tracking-wide text-emerald-700">{point.note}</p>}
                </div>
              </div>
            </motion.div>
          ))}
          {(mainPoint?.mapUrl || contact.mapUrl) && (
            <a href={extractIframeSrc(mainPoint?.mapUrl || contact.mapUrl)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-3 text-sm font-bold text-white shadow hover:bg-emerald-800">
              <ExternalLink size={18} /> Open Map
            </a>
          )}
        </div>
      </div>
    </Section>
  );
}

export default function UnitClassicPage({ data = {} }) {
  const reduceMotion = useReducedMotion();
  const title = data.title || data.shortName || "Unit";
  const heroImage = safeImage(data.heroImage, "/images/antib.jpg");
  const overview = toParagraphs(data.overview);
  const leader = data.leader || null;
  const contact = data.contact || {};
  const quickStats = data.quickStats || [];
  const quickInfo = data.quickInfo || [];
  const offerings = data.offerings || [];
  const features = data.features || [];
  const timeline = data.timeline || [];
  const downloads = data.downloads || [];
  const gallery = data.gallery || [];
  const faqs = data.faqs || [];
  const mapPoints = data.mapPoints || [];
  const progress = leader?.progress || (leader?.achievements || []).map((item) => ({ title: item, icon: "🛡️" }));

  return (
    <main className="unit-classic-page bg-slate-50 text-slate-950">
      <header className="relative isolate overflow-hidden bg-emerald-950 text-white">
        <motion.img src={heroImage} alt="" className="absolute inset-0 -z-20 h-full w-full object-cover" initial={reduceMotion ? false : { scale: 1.08 }} animate={reduceMotion ? undefined : { scale: 1.02 }} transition={{ duration: 4, ease: [0.22, 1, 0.36, 1] }} />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-emerald-950 via-slate-950/88 to-green-900/70" />
        <div className="absolute inset-0 -z-10 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 20% 25%, #86efac 0, transparent 22%), radial-gradient(circle at 85% 20%, #22c55e 0, transparent 20%)" }} />
        <motion.div variants={stagger} initial="hidden" animate="visible" className="mx-auto max-w-7xl px-4 py-20 md:py-28">
          <motion.div variants={fadeUp} className="flex flex-wrap gap-2">
            <span className="rounded-full border border-white/20 bg-white/15 px-4 py-2 text-xs font-black uppercase tracking-widest backdrop-blur">Institutional Unit</span>
            {data.parentGroup && <span className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-bold backdrop-blur">{data.parentGroup}</span>}
          </motion.div>
          <motion.h1 variants={fadeUp} className="mt-6 max-w-5xl text-4xl font-black tracking-tight md:text-6xl">{title}</motion.h1>
          <motion.p variants={fadeUp} className="mt-5 max-w-3xl text-lg leading-8 text-white/90">{data.subtitle || "Supporting institutional service delivery, compliance, administration and public trust."}</motion.p>
          <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-3">
            {data.primaryAction && <a href={data.primaryAction.href} className="rounded-xl bg-white px-5 py-3 font-black text-emerald-800 shadow-lg transition hover:-translate-y-1 hover:bg-emerald-50">{data.primaryAction.label}</a>}
            {data.secondaryAction && <a href={data.secondaryAction.href} className="rounded-xl border border-white/30 bg-white/10 px-5 py-3 font-black text-white backdrop-blur transition hover:-translate-y-1 hover:bg-white/20">{data.secondaryAction.label}</a>}
          </motion.div>
          {quickStats.length > 0 && (
            <motion.div variants={stagger} className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {quickStats.map((stat, index) => (
                <motion.div key={index} variants={fadeUp} whileHover={reduceMotion ? undefined : { y: -5 }} className="rounded-2xl border border-white/20 bg-white/15 p-4 shadow-lg backdrop-blur">
                  <p className="text-2xl font-black">{stat.value}</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wider text-white/75">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>
          )}
        </motion.div>
      </header>

      <div className="mx-auto max-w-7xl space-y-8 px-4 py-10 md:py-14">
        <Section id="overview" eyebrow="Mandate" title={data.overviewTitle || "Unit Overview"} icon={ShieldCheck}>
          <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
            <div className="space-y-4 text-base leading-8 text-slate-700">
              {(overview.length ? overview : ["This unit supports institutional administration, compliance, service quality and stakeholder support. Full details can be updated from the admin dashboard."]).map((paragraph, index) => <p key={index}>{paragraph}</p>)}
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <h3 className="mb-4 font-black text-slate-900">Quick Information</h3>
              <div className="space-y-3">
                {(quickInfo.length ? quickInfo : [
                  { label: "Type", value: "Institutional Unit" },
                  { label: "Location", value: contact.address || "Federal Polytechnic Bida" },
                ]).map((info, index) => (
                  <div key={index} className="flex items-start justify-between gap-4 border-b border-slate-200 pb-3 last:border-b-0 last:pb-0">
                    <span className="text-sm font-bold text-slate-500">{info.label}</span>
                    <span className="text-right text-sm font-black text-slate-900">{info.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {(data.vision || data.mission) && (
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {data.vision && <div className="rounded-2xl bg-emerald-50 p-5"><h3 className="font-black text-emerald-900">Vision</h3><p className="mt-2 text-sm leading-7 text-slate-700">{data.vision}</p></div>}
              {data.mission && <div className="rounded-2xl bg-lime-50 p-5"><h3 className="font-black text-lime-900">Mission</h3><p className="mt-2 text-sm leading-7 text-slate-700">{data.mission}</p></div>}
            </div>
          )}
        </Section>

        {leader && (
          <Section id="leader" eyebrow="Leadership" title={leader.sectionTitle || "Unit Leadership"} icon={Users}>
            <ProgressBadges items={progress} reduceMotion={reduceMotion} />
            <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 text-center">
                <img src={safeImage(leader.photo)} alt={leader.name || "Unit leader"} className="mx-auto h-36 w-36 rounded-3xl object-cover shadow-lg" />
                <h3 className="mt-4 text-xl font-black text-slate-950">{leader.name}</h3>
                <p className="mt-1 text-sm font-bold text-emerald-700">{leader.title}</p>
                {leader.badge && <span className="mt-3 inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-black text-emerald-800">{leader.badge}</span>}
              </div>
              <div className="rounded-3xl bg-gradient-to-br from-emerald-50 to-white p-6 ring-1 ring-emerald-100">
                {leader.quote && <blockquote className="border-l-4 border-emerald-600 pl-4 text-lg font-black leading-8 text-slate-900">“{leader.quote}”</blockquote>}
                <p className="mt-5 leading-8 text-slate-700">{leader.bio || "The unit leadership coordinates functions, service delivery and stakeholder support for the Polytechnic community."}</p>
              </div>
            </div>
          </Section>
        )}

        {offerings.length > 0 && (
          <Section id="offerings" eyebrow="Functions" title={data.offeringsTitle || "Unit Functions and Responsibilities"} icon={Target} dark>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {offerings.map((item, index) => (
                <motion.div key={index} whileHover={reduceMotion ? undefined : { y: -6 }} className="rounded-2xl border border-white/10 bg-white/10 p-5 shadow-sm backdrop-blur">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 font-black text-emerald-100">{String(index + 1).padStart(2, "0")}</span>
                  <h3 className="mt-4 text-lg font-black text-white">{item.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-white/80">{item.description}</p>
                  {item.meta && <p className="mt-3 text-xs font-black uppercase tracking-wider text-emerald-200">{item.meta}</p>}
                </motion.div>
              ))}
            </div>
          </Section>
        )}

        {features.length > 0 && (
          <Section id="impact" eyebrow="Impact" title={data.featuresTitle || "Why the Unit Matters"} icon={CheckCircle2}>
            <div className="grid gap-4 md:grid-cols-3">
              {features.map((item, index) => (
                <motion.div key={index} whileHover={reduceMotion ? undefined : { y: -5 }} className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-sm">
                  <ShieldCheck size={22} className="text-emerald-700" />
                  <h3 className="mt-4 text-lg font-black text-slate-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-slate-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </Section>
        )}

        {timeline.length > 0 && (
          <Section id="timeline" eyebrow="Progress" title="Milestones" icon={Clock}>
            <div className="space-y-3">
              {timeline.map((item, index) => (
                <div key={index} className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-[140px_1fr]">
                  <p className="font-black text-emerald-700">{item.year || item.value}</p>
                  <div><h3 className="font-black text-slate-900">{item.title}</h3><p className="mt-1 text-sm leading-6 text-slate-600">{item.description}</p></div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {downloads.length > 0 && (
          <Section id="downloads" eyebrow="Resources" title="Forms, Guidelines and Useful Links" icon={Download}>
            <div className="grid gap-3 md:grid-cols-2">
              {downloads.map((item, index) => (
                <a key={index} href={item.href || item.url || "/downloads"} className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 font-bold text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50">
                  <span className="flex items-center gap-3"><FileText size={18} className="text-emerald-700" /> {item.label || item.title}</span>
                  <Download size={18} />
                </a>
              ))}
            </div>
          </Section>
        )}

        {gallery.length > 0 && (
          <Section id="gallery" eyebrow="Media" title="Unit Gallery" icon={ImageIcon}>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {gallery.map((item, index) => (
                <motion.figure key={index} whileHover={reduceMotion ? undefined : { y: -6 }} className="group overflow-hidden rounded-2xl bg-slate-900 shadow-lg">
                  <img src={safeImage(item.src || item.image_url)} alt={item.caption || "Unit gallery"} className="h-44 w-full object-cover transition duration-500 group-hover:scale-105" />
                  <figcaption className="p-4 text-sm font-bold text-white">{item.caption || item.title}</figcaption>
                </motion.figure>
              ))}
            </div>
          </Section>
        )}

        {faqs.length > 0 && (
          <Section id="faq" eyebrow="FAQ" title="Frequently Asked Questions" icon={HelpCircle}>
            <div className="space-y-3">
              {faqs.map((item, index) => (
                <details key={index} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <summary className="cursor-pointer font-black text-slate-900">{item.question}</summary>
                  <p className="mt-3 text-sm leading-7 text-slate-600">{item.answer}</p>
                </details>
              ))}
            </div>
          </Section>
        )}

        <UnitMapSection contact={contact} mapPoints={mapPoints} reduceMotion={reduceMotion} />

        <Section id="contact" eyebrow="Contact" title={contact.title || "Contact the Unit"} icon={Mail}>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl bg-slate-50 p-5"><MapPin className="text-emerald-700" /><h3 className="mt-3 font-black">Address</h3><p className="mt-2 text-sm leading-6 text-slate-600">{contact.address || "Federal Polytechnic Bida"}</p></div>
            <div className="rounded-2xl bg-slate-50 p-5"><Phone className="text-emerald-700" /><h3 className="mt-3 font-black">Phone</h3><p className="mt-2 text-sm leading-6 text-slate-600">{contact.phone || "Not specified"}</p></div>
            <div className="rounded-2xl bg-slate-50 p-5"><Mail className="text-emerald-700" /><h3 className="mt-3 font-black">Email</h3><p className="mt-2 break-words text-sm leading-6 text-slate-600">{contact.email || "Not specified"}</p></div>
            <div className="rounded-2xl bg-slate-50 p-5"><Clock className="text-emerald-700" /><h3 className="mt-3 font-black">Hours</h3><p className="mt-2 text-sm leading-6 text-slate-600">{contact.hours || "Monday - Friday"}</p></div>
          </div>
        </Section>
      </div>
    </main>
  );
}
